import React, { createContext, useContext, useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Link, useNavigate } from 'react-router-dom';
import { adminAuthAPI } from './api';
import { APP_VERSION } from './version';
import Login         from './pages/Login';
import Overview      from './pages/Overview';
import Businesses    from './pages/Businesses';
import Users         from './pages/Users';
import Activity      from './pages/Activity';
import Settings      from './pages/Settings';
import Releases      from './pages/Releases';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword  from './pages/ResetPassword';
import ForcePasswordChange from './pages/ForcePasswordChange';
import './App.css';

const AdminAuthContext = createContext(null);
export const useAdminAuth = () => useContext(AdminAuthContext);

function clearHint() {
  try { localStorage.removeItem('shopbook_admin'); } catch { /* storage blocked */ }
}

function AdminAuthProvider({ children }) {
  const [admin,   setAdmin]   = useState(null);
  const [loading, setLoading] = useState(true);

  // 'shopbook_admin' is only a hint that a session may exist (name/email, no
  // secret). The server decides: /auth/me succeeds only with a valid cookie.
  useEffect(() => {
    let hinted = false;
    try { hinted = !!localStorage.getItem('shopbook_admin'); } catch { /* storage blocked */ }
    if (!hinted) { setLoading(false); return; }
    adminAuthAPI.me()
      .then(({ data }) => { setAdmin(data.admin); })
      .catch(() => { clearHint(); })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const onSignedOut = () => { clearHint(); setAdmin(null); };
    window.addEventListener('shopbook-admin-signed-out', onSignedOut);
    return () => window.removeEventListener('shopbook-admin-signed-out', onSignedOut);
  }, []);

  const login  = (adminData) => {
    try { localStorage.setItem('shopbook_admin', JSON.stringify(adminData)); } catch { /* storage blocked */ }
    setAdmin(adminData);
  };
  // Revoke the session on the server; sign out locally even if that fails.
  const logout = async () => {
    try { await adminAuthAPI.logout(); } catch { /* already signed out */ }
    clearHint();
    setAdmin(null);
  };

  return <AdminAuthContext.Provider value={{ admin, loading, login, logout }}>{children}</AdminAuthContext.Provider>;
}

function Layout({ children }) {
  const { admin, logout } = useAdminAuth();
  const navigate = useNavigate();
  const handleLogout = async () => { await logout(); navigate('/'); };
  return (
    <div className="admin-layout">
      <aside className="admin-sidebar">
        <div className="admin-logo">ShopBook Admin</div>
        <nav>
          <Link to="/dashboard">Dashboard</Link>
          <Link to="/businesses">Businesses</Link>
          <Link to="/users">Users</Link>
          <Link to="/activity">Activity</Link>
          <Link to="/releases">Releases</Link>
          <Link to="/settings">Settings</Link>
        </nav>
        <div className="admin-user">
          <span>{admin?.name}</span>
          <button onClick={handleLogout}>Logout</button>
          <Link to="/releases" className="admin-version">v{APP_VERSION}</Link>
        </div>
      </aside>
      <main className="admin-main">{children}</main>
    </div>
  );
}

function PrivateRoute({ children }) {
  const { admin, loading } = useAdminAuth();
  if (loading) return <p style={{ padding: '2rem' }}>Loading…</p>;
  return admin ? children : <Navigate to="/" replace />;
}

export default function App() {
  const { admin } = useAdminAuth() || {};
  return (
    <AdminAuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/"                element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password"  element={<ResetPassword />} />
          <Route path="/force-password-change" element={<ForcePasswordChange />} />
          <Route path="/dashboard"   element={<PrivateRoute><Layout><Overview /></Layout></PrivateRoute>} />
          <Route path="/businesses"  element={<PrivateRoute><Layout><Businesses /></Layout></PrivateRoute>} />
          <Route path="/users"       element={<PrivateRoute><Layout><Users /></Layout></PrivateRoute>} />
          <Route path="/activity"    element={<PrivateRoute><Layout><Activity /></Layout></PrivateRoute>} />
          <Route path="/releases"    element={<PrivateRoute><Layout><Releases /></Layout></PrivateRoute>} />
          <Route path="/settings"    element={<PrivateRoute><Layout><Settings /></Layout></PrivateRoute>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AdminAuthProvider>
  );
}
